import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface StatusConfig {
  i18nKey: string;
  color: string;
}

const STATUS_CONFIG: Record<string, StatusConfig> = {
  pending: { i18nKey: 'status.pending', color: '#f59e0b' },
  in_progress: { i18nKey: 'status.inProgress', color: '#3b82f6' },
  processing: { i18nKey: 'status.processing', color: '#3b82f6' },
  completed: { i18nKey: 'status.completed', color: '#10b981' },
  cancelled: { i18nKey: 'status.cancelled', color: '#ef4444' },
  closed: { i18nKey: 'status.closed', color: '#6b7280' },
  archived: { i18nKey: 'status.archived', color: '#6b7280' },
  on_hold: { i18nKey: 'status.onHold', color: '#f59e0b' },
};

const FALLBACK_CONFIG: StatusConfig = { i18nKey: 'status.unknown', color: '#6b7280' };

export const getStatusConfig = (status?: string | null): StatusConfig => {
  const key = (status || 'pending').toLowerCase();
  return STATUS_CONFIG[key] || FALLBACK_CONFIG;
};

export const getStatusLabel = (t: any, status?: string | null): string => {
  const key = (status || 'pending').toLowerCase();
  const config = STATUS_CONFIG[key];
  return config ? t(config.i18nKey) : status || t(FALLBACK_CONFIG.i18nKey);
};

export const getStatusFilterOptions = (t: any): { value: string; label: string }[] => [
  { value: 'pending', label: t(STATUS_CONFIG.pending.i18nKey) },
  { value: 'in_progress', label: t(STATUS_CONFIG.in_progress.i18nKey) },
  { value: 'completed', label: t(STATUS_CONFIG.completed.i18nKey) },
  { value: 'cancelled', label: t(STATUS_CONFIG.cancelled.i18nKey) },
];

interface StatusBadgeProps {
  status?: string | null;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'small', sx }) => {
  const { t } = useTranslation();
  const { color } = getStatusConfig(status);
  const label = getStatusLabel(t, status);
  const isSmall = size === 'small';

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: isSmall ? 1 : 1.25,
        py: isSmall ? 0.25 : 0.5,
        borderRadius: 10,
        bgcolor: `${color}1a`,
        color,
        border: `1px solid ${color}33`,
        fontSize: isSmall ? '0.75rem' : '0.8125rem',
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      <Box
        component="span"
        sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
      />
      {label}
    </Box>
  );
};

export default StatusBadge;
